// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  /** Shown muted after the label, e.g. how many apps an option matches. */
  count?: number;
  disabled?: boolean;
}

interface FilterOptionListProps {
  /** Names the listbox for assistive tech. */
  label: string;
  options: FilterOption[];
  selected: string[];
  onSelect: (value: string) => void;
  /** Shows a search row that narrows the options by label. */
  searchPlaceholder?: string;
  multiple?: boolean;
}

/**
 * The list inside each filter popover: an optional search row over a listbox,
 * driven from the keyboard by arrows, Home/End and Enter. Focus stays on the
 * search input (or the list itself when there is none), and the highlighted
 * option is announced through aria-activedescendant.
 */
export function FilterOptionList({
  label,
  options,
  selected,
  onSelect,
  searchPlaceholder,
  multiple = false,
}: FilterOptionListProps): React.JSX.Element {
  const id = React.useId();
  const [search, setSearch] = React.useState('');
  const visible = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
    : options;
  const [highlight, setHighlight] = React.useState(() =>
    visible.findIndex((o) => selected.includes(o.value)),
  );
  const listRef = React.useRef<HTMLDivElement>(null);
  const optionId = (index: number) => `${id}-option-${index}`;

  React.useEffect(() => {
    if (highlight < 0) return;
    document.getElementById(optionId(highlight))?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight]);

  const move = (from: number, step: 1 | -1) => {
    for (let i = 1; i <= visible.length; i++) {
      const next = (from + step * i + visible.length * 2) % visible.length;
      if (!visible[next]?.disabled) return next;
    }
    return -1;
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (visible.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        setHighlight((h) => move(h < 0 ? -1 : h, 1));
        break;
      case 'ArrowUp':
        setHighlight((h) => move(h < 0 ? 0 : h, -1));
        break;
      case 'Home':
        setHighlight(move(-1, 1));
        break;
      case 'End':
        setHighlight(move(0, -1));
        break;
      case 'Enter': {
        const option = visible[highlight];
        if (option && !option.disabled) onSelect(option.value);
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  const activeDescendant = highlight >= 0 ? optionId(highlight) : undefined;

  return (
    <div className="flex min-h-0 w-full flex-col">
      {searchPlaceholder && (
        <div className="flex h-10 shrink-0 items-center border-b border-border/60 px-1.5 py-1">
          <input
            autoFocus
            role="combobox"
            aria-expanded
            aria-controls={`${id}-list`}
            aria-activedescendant={activeDescendant}
            aria-label={label}
            placeholder={searchPlaceholder}
            autoComplete="off"
            spellCheck={false}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setHighlight(-1);
            }}
            onKeyDown={onKeyDown}
            className="h-full w-full min-w-0 flex-1 bg-transparent px-1.5 text-sm outline-hidden placeholder:text-muted-foreground"
          />
        </div>
      )}
      <div
        ref={listRef}
        id={`${id}-list`}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple || undefined}
        aria-activedescendant={searchPlaceholder ? undefined : activeDescendant}
        tabIndex={searchPlaceholder ? -1 : 0}
        autoFocus={!searchPlaceholder}
        onKeyDown={searchPlaceholder ? undefined : onKeyDown}
        className="max-h-[260px] overflow-y-auto overscroll-contain p-1 outline-hidden"
      >
        {visible.length === 0 ? (
          <div className="py-2 text-center text-sm text-muted-foreground">No results</div>
        ) : (
          visible.map((option, index) => {
            const isSelected = selected.includes(option.value);
            return (
              <div
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                data-highlighted={index === highlight || undefined}
                data-disabled={option.disabled || undefined}
                // Keep focus in the search input or list while clicking.
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => !option.disabled && setHighlight(index)}
                onClick={() => !option.disabled && onSelect(option.value)}
                className={cn(
                  'relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1 ps-1.5 pe-8 text-sm outline-hidden',
                  'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
                  'data-disabled:pointer-events-none data-disabled:opacity-50',
                  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
                )}
              >
                {option.icon}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-xs tabular-nums text-muted-foreground">{option.count}</span>
                )}
                {isSelected && (
                  <span className="pointer-events-none absolute end-1.5 flex size-4 items-center justify-center">
                    <Check aria-hidden />
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
